import type { ApiError, ConflictApiError } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { RegularFilter, RegularFilterSelectedValues } from '../../models'
import type { IToolingBaseComposableDataItem } from '../use-tooling-base/types'
import type {
  IToolingFormComposableMethods,
  IToolingFormComposableMethodsParams,
  IToolingFormComposableOnAcceptOptions,
  IToolingFormComposableProps,
  IToolingFormComposableResolveConflictOptions,
} from './types'
// @ts-strict-ignore
import { DomainObjectUtils } from '@empathyco/playboard-core-domain'
import Vue, { getCurrentInstance } from 'vue'
import { ResolveConflictOption } from '../../models/components/resolve-conflict-option'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { TOOLING_CONSTS } from '../../store/tooling/tooling.consts'
import { FiltersUtils } from '../../utils/filters.utils'
import { useApiError } from '../use-api-error'
import { useToolingPivots } from '../use-tooling-pivots'
import { mapActions } from '../utils'

export const methodsFn = <DataItemType extends IToolingBaseComposableDataItem>(
  props: IToolingFormComposableProps,
  ctx: SetupContext,
  params: IToolingFormComposableMethodsParams<DataItemType>,
): IToolingFormComposableMethods<DataItemType> => {
  const { emit } = ctx
  const {
    proxy: { $store, $router, $route },
  } = getCurrentInstance()

  const { getErrorMessage, isConflictError } = useApiError()

  const { pivots, setPivotSelectedValues, getPivotDefaultValues } = useToolingPivots({
    pivotable: params.pivotable,
    extraConfig: params.pivotsExtraConfig,
  })

  const { readAction, createAction, updateAction } = mapActions($store, TOOLING_CONSTS.moduleName, {
    readAction: TOOLING_CONSTS.actions.read,
    createAction: TOOLING_CONSTS.actions.create,
    updateAction: TOOLING_CONSTS.actions.update,
  })

  let conflictingElementIds: Array<string> = []

  const getItemFromState = (id: string): DataItemType => {
    let item = params.data.value.results.find(
      (dataItem: DataItemType) => dataItem.id.toString() === id,
    )
    if (!item) {
      const defaultValue = params.toolConfig.value.default
      if (defaultValue?.id === id) {
        item = defaultValue
      }
    }
    return item
  }

  const getItemFromService = async (id: string): Promise<DataItemType> => {
    emit('updateLoading', { visible: true })
    try {
      const itemFromService = await readAction({ tool: params.featureId.value, id })
      emit('updateLoading', { visible: false })
      return itemFromService
    } catch (error) {
      if (error.status === 404) {
        $router.replace({
          ...$route,
          name: $route.meta.parentRouteName,
        })
      } else {
        emit('showError', getErrorMessage(error))
        emit('updateLoading', { visible: false })
      }
    }
  }

  const onPivotChange = (
    pivot: RegularFilter,
    selectedValues: RegularFilterSelectedValues,
  ): void => {
    setPivotSelectedValues(pivot, selectedValues)
    const previewFilter = params.previewFilters.value[pivot.config.id]
    if (
      !!previewFilter.selectedValues.length &&
      !previewFilter.config.values.some(({ id }) => id === previewFilter.selectedValues[0])
    ) {
      Vue.set(params.previewFiltersSelectedValues.value, previewFilter.config.id, [
        previewFilter.config.values[0].id,
      ])
    }
  }

  const initItemPivotValues = (): void => {
    pivots.value.forEach(pivot => {
      if (!pivot.selectedValues.length) {
        const pivotDefaultValues = getPivotDefaultValues(pivot)
        if (pivotDefaultValues.length) {
          setPivotSelectedValues(pivot, pivotDefaultValues)
        }
      }
    })
  }

  const initPreviewFiltersSelectedValues = (): void => {
    params.previewFiltersSelectedValues.value = Object.values(params.previewFilters.value).reduce(
      (result: Record<string, RegularFilterSelectedValues>, filter: RegularFilter) => {
        return {
          ...result,
          [filter.config.id]: FiltersUtils.getDefaultSelectedValues(filter, true),
        }
      },
      {},
    )
  }

  const isCloneMode = () => !!$route.params[ROUTER_CONSTS.params.id] && !params.updateMode

  const initItem = async ({
    forceFetch = false,
  }: {
    forceFetch?: boolean
  } = {}): Promise<void> => {
    const id = $route.params[ROUTER_CONSTS.params.id]
    if (id) {
      let item: DataItemType
      if (!forceFetch) {
        item = getItemFromState(id)
      }
      if (!item) {
        item = await getItemFromService(id)
      }
      if (!params.updateMode) {
        item.id = undefined
      }
      params.item.value = DomainObjectUtils.clone(item)
    }
    initItemPivotValues()
    initPreviewFiltersSelectedValues()
  }

  const onAccept = async function (
    options: IToolingFormComposableOnAcceptOptions = {},
    updatePrevious: DataItemType | Array<DataItemType> = undefined,
  ): Promise<void> {
    emit('updateLoading', { visible: true })
    conflictingElementIds = []
    try {
      if (updatePrevious) {
        const itemsToUpdate = Array.isArray(updatePrevious) ? updatePrevious : [updatePrevious]
        await updateAction({
          tool: params.featureId.value,
          items: itemsToUpdate,
        })
      }
      if (!params.updateMode) {
        await createAction({
          tool: params.featureId.value,
          item: params.item.value,
        })
        emit('showSnackbar', params.messages.create.success)
      } else {
        await updateAction({
          tool: params.featureId.value,
          items: [params.item.value],
        })
        emit('showSnackbar', params.messages.update.success)
      }
      $router.push({
        ...$route,
        name: $route.meta.parentRouteName,
      })
    } catch (errors) {
      const error: ApiError = Array.isArray(errors) ? errors[0] : errors
      if (isConflictError(error)) {
        conflictingElementIds = (error as ConflictApiError).conflictingElements.map(
          conflictingElement => conflictingElement.id,
        )
      }
      if (typeof options.errorHandler === 'function') {
        options.errorHandler(error)
      } else {
        emit('showError', getErrorMessage(error))
      }
    } finally {
      emit('updateLoading', { visible: false })
    }
  }

  const onCancel = (): void => {
    $router.push({
      ...$route,
      name: $route.meta.parentRouteName,
    })
  }

  async function resolveConflict({
    resolveConflictOption,
    errorHandler,
    conflictingItems,
  }: IToolingFormComposableResolveConflictOptions<DataItemType>): Promise<void> {
    if (resolveConflictOption === ResolveConflictOption.DISABLE_CURRENT) {
      params.item.value.criteria.enabled = false
      await onAccept({ errorHandler })
    } else {
      const itemsToDisable = conflictingItems ?? (await getItemsById(conflictingElementIds))
      const disabledItems = itemsToDisable.map(item => {
        const clonedItem = DomainObjectUtils.clone(item)
        clonedItem.criteria.enabled = false
        return clonedItem
      })
      await onAccept({ errorHandler }, disabledItems)
    }
  }

  async function getItemsById(ids: string[]): Promise<Array<DataItemType>> {
    const promises = await Promise.allSettled(
      ids.map(id => readAction({ tool: params.featureId.value, id })),
    )
    const items = []
    promises.forEach(promise => {
      if (promise.status === 'fulfilled') {
        items.push(promise.value)
      }
    })

    return items
  }

  return {
    initItem,
    onPivotChange,
    onAccept,
    onCancel,
    resolveConflict,
    isCloneMode,
  }
}
